import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { X, Loader2, CheckCircle2, AlertCircle, FileCheck } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api/admin`;

/**
 * Admin-side Edit KYC modal for an agent. Captures:
 *   • has_gstin checkbox
 *   • GSTIN (required if has_gstin, validated against state prefix)
 *   • state + state_code (required if has_gstin)
 *   • PAN (ALWAYS required, per business rule)
 *   • billing_address (optional)
 *
 * On save, calls PATCH /api/admin/agents/{id}/tax.
 */
const AgentKycModal = ({ agent, onClose, onSaved }) => {
  const [states, setStates] = useState([]);
  const [has_gstin, setHasGstin] = useState(!!agent?.has_gstin);
  const [gstin, setGstin] = useState(agent?.gstin || '');
  const [state_code, setStateCode] = useState(agent?.state_code || '27');
  const [state, setState] = useState(agent?.state || 'Maharashtra');
  const [pan, setPan] = useState(agent?.pan || '');
  const [billing_address, setBillingAddress] = useState(agent?.billing_address || '');
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const r = await axios.get(`${API}/settings/states`, { withCredentials: true });
        setStates(r.data.states || []);
      } catch (_e) { /* silent */ }
    })();
  }, []);

  // GSTIN prefix auto-matches state_code
  const onGstinChange = (v) => {
    const val = v.toUpperCase().replace(/\s/g, '').slice(0, 15);
    setGstin(val);
    if (val.length >= 2) {
      const prefix = val.slice(0, 2);
      const match = states.find((s) => s.code === prefix);
      if (match) {
        setStateCode(prefix);
        setState(match.name);
      }
    }
  };

  const onStateSelect = (code) => {
    setStateCode(code);
    const match = states.find((s) => s.code === code);
    setState(match?.name || '');
  };

  const save = async () => {
    setErr(null);
    setSaving(true);
    try {
      const payload = {
        has_gstin,
        pan: pan.toUpperCase().trim(),
        billing_address: billing_address.trim(),
      };
      if (has_gstin) {
        payload.gstin = gstin.toUpperCase().trim();
        payload.state_code = state_code;
        payload.state = state;
      }
      const r = await axios.patch(`${API}/agents/${agent.id}/tax`, payload, { withCredentials: true });
      onSaved?.(r.data.agent);
    } catch (e) {
      setErr(e?.response?.data?.detail || 'Save failed — check fields and try again.');
    } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" data-testid="agent-kyc-modal">
      <div className="bg-white rounded-xl w-full max-w-2xl max-h-[92vh] overflow-y-auto">
        <div className="flex items-center justify-between p-4 md:p-5 border-b border-gray-200">
          <div className="flex items-center gap-2">
            <FileCheck className="w-5 h-5 text-[#0C1D36]" />
            <h3 className="text-lg font-semibold text-gray-900">Edit KYC — {agent?.full_name || agent?.email}</h3>
          </div>
          <button onClick={onClose} className="p-1.5 hover:bg-gray-100 rounded" data-testid="agent-kyc-close">
            <X className="w-5 h-5 text-gray-500" />
          </button>
        </div>

        <div className="p-4 md:p-5 space-y-4">
          {err && (
            <div className="px-3 py-2 bg-red-50 text-red-800 text-sm rounded-lg flex items-start gap-2">
              <AlertCircle className="w-4 h-4 flex-shrink-0 mt-0.5" /> {err}
            </div>
          )}

          {/* GSTIN toggle */}
          <label className="flex items-start gap-2 cursor-pointer bg-blue-50 border border-blue-200 rounded-lg p-3">
            <input
              type="checkbox"
              checked={has_gstin}
              onChange={(e) => setHasGstin(e.target.checked)}
              className="mt-1"
              data-testid="agent-kyc-has-gstin"
            />
            <div className="text-sm text-blue-900">
              <div className="font-semibold">This agent is GST-registered</div>
              <div className="text-xs mt-0.5 text-blue-700">
                When checked, Tax Invoices will be auto-generated for every booking with CGST/SGST or IGST on the markup. Uncheck for walk-in (no invoice).
              </div>
            </div>
          </label>

          {/* GSTIN fields — only if checked */}
          {has_gstin && (
            <div className="space-y-3 bg-gray-50 rounded-lg p-3 border border-gray-200">
              <Field label="GSTIN (15 characters)">
                <input
                  type="text"
                  value={gstin}
                  onChange={(e) => onGstinChange(e.target.value)}
                  maxLength={15}
                  placeholder="27AAAAA0000A1Z5"
                  className={inputCls + " font-mono uppercase"}
                  data-testid="agent-kyc-gstin"
                />
                {gstin.length > 0 && gstin.length !== 15 && (
                  <div className="text-xs text-amber-700 mt-1">Expected 15 characters, got {gstin.length}.</div>
                )}
              </Field>
              <Field label="State">
                <select
                  value={state_code}
                  onChange={(e) => onStateSelect(e.target.value)}
                  className={inputCls}
                  data-testid="agent-kyc-state"
                >
                  {states.map((s) => (
                    <option key={s.code} value={s.code}>{s.code} · {s.name}</option>
                  ))}
                </select>
                {gstin.length >= 2 && gstin.slice(0, 2) !== state_code && (
                  <div className="text-xs text-red-600 mt-1">
                    GSTIN starts with <b>{gstin.slice(0, 2)}</b> — doesn't match selected state <b>{state_code}</b>.
                  </div>
                )}
              </Field>
            </div>
          )}

          {/* PAN — always required */}
          <Field label="PAN (10 characters — always required)">
            <input
              type="text"
              value={pan}
              onChange={(e) => setPan(e.target.value.toUpperCase().slice(0, 10))}
              maxLength={10}
              placeholder="AAAAA1234A"
              className={inputCls + " font-mono uppercase"}
              data-testid="agent-kyc-pan"
            />
          </Field>

          <Field label="Billing Address (optional)">
            <textarea
              rows={2}
              value={billing_address}
              onChange={(e) => setBillingAddress(e.target.value)}
              className={inputCls}
              data-testid="agent-kyc-address"
            />
          </Field>
        </div>

        <div className="p-4 md:p-5 border-t border-gray-200 flex justify-end gap-2 bg-gray-50">
          <button
            onClick={onClose}
            className="px-4 py-2 border border-gray-300 rounded-lg text-sm hover:bg-white"
            data-testid="agent-kyc-cancel"
          >
            Cancel
          </button>
          <button
            onClick={save}
            disabled={saving}
            className="flex items-center gap-2 px-5 py-2 bg-[#0C1D36] text-white rounded-lg hover:bg-[#0C1D36]/90 disabled:opacity-60 text-sm font-semibold"
            data-testid="agent-kyc-save"
          >
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle2 className="w-4 h-4" />} Save KYC
          </button>
        </div>
      </div>
    </div>
  );
};

const inputCls = "w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#F5A623]";

const Field = ({ label, children }) => (
  <div>
    <label className="block text-xs font-semibold text-gray-700 mb-1 uppercase tracking-wide">{label}</label>
    {children}
  </div>
);

export default AgentKycModal;
